// src/components/header-client.ts
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./credito-header";

import "./carrito-cliente";
import "./info-cliente";
import logo from "../../assets/logos/DSIsotipoRGB1.svg";

interface SubOpcion {
  texto: string;
  url: string;
}
interface Opcion {
  texto: string;
  url: string;
  options?: SubOpcion[] | null;
  isHome?: boolean;
}
interface MenuPermiso {
  codigo: string;
  nombre: string;
  descripcion: string;
}
interface MenuOpcion {
  nombre: string;
  url: string;
  orden: number;
  permisos: MenuPermiso[];
}
interface MenuItem {
  nombre: string;
  url: string | null;
  orden: number;
  permisos: MenuPermiso[];
  opciones?: MenuOpcion[];
}
interface UsuarioData {
  cod_farmacia: string;
  nombre: string;
  mail: string;
  dto_cliente: string | number;
  grupo?: string | number;
  usuario_nombre?: string;
  rol: string;
  menu: MenuItem[];
}
export type Rol =
  | "vendedor"
  | "cliente"
  | "usuario"
  | "admin"
  | "cliente_admin"
  | "cliente_user"
  | "invitado";

@customElement("header-client")
export class HeaderClient extends LitElement {
  @property({ type: String }) usuario: Rol = "invitado";

  @state() private isDarkTheme = false;
  @state() private userData: UsuarioData | null = null;
  @state() private isSweetAlertActive = false;
  @state() private scrolled = false;
  @state() private currentHash = "";

  private _sweetAlertObserver?: MutationObserver;
  private _classObserver?: MutationObserver;
  private _scrollRAF = 0;

  /* =================== ESTILOS CONSOLIDADOS =================== */
  /* Todos los estilos para header-client han sido consolidados en */
  /* global.css (Sección 21: ESTILOS CONSOLIDADOS PARA HEADER-CLIENT) */
  /* El componente utiliza Light DOM, por lo que los estilos globales */
  /* se aplican automáticamente sin necesidad de static styles. */
  static styles = css``;

  /* ========= Light DOM + inyección del CSS ========= */
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.ensureGlobalStyles();
    this.loadUserData();
    this.setupTheme();
    this.setupEventListeners(true);
    this.setupSweetAlertDetection(true);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.setupEventListeners(false);
    this.setupSweetAlertDetection(false);
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
  }
  private ensureGlobalStyles() {
    const id = "header-client-global-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      // @ts-ignore
      s.textContent = (this.constructor as typeof HeaderClient).styles.cssText;
      document.head.appendChild(s);
    }
  }

  /* ========= SweetAlert detection ========= */
  private setupSweetAlertDetection(enable: boolean) {
    if (enable) {
      this._classObserver = new MutationObserver(() => {
        const active =
          document.body.classList.contains("swal2-shown") ||
          document.documentElement.classList.contains("swal2-shown") ||
          !!document.querySelector(".swal2-container");
        this.isSweetAlertActive = active;
      });
      this._classObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
      this._classObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });

      this._sweetAlertObserver = new MutationObserver(() => {
        this.isSweetAlertActive = !!document.querySelector(".swal2-container");
      });
      this._sweetAlertObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      this.isSweetAlertActive =
        document.body.classList.contains("swal2-shown") ||
        !!document.querySelector(".swal2-container");
    } else {
      this._classObserver?.disconnect();
      this._sweetAlertObserver?.disconnect();
      this._classObserver = undefined;
      this._sweetAlertObserver = undefined;
    }
  }

  /* ========= Theme (alineado con auth-service) ========= */
  private setupTheme() {
    // Migrar claves legacy si existen y luego borrarlas
    if (!localStorage.getItem("client-theme")) {
      const legacy =
        localStorage.getItem("theme-preference") ||
        localStorage.getItem("theme") ||
        (localStorage.getItem("darkMode") === "true" ? "dark" : null) ||
        (localStorage.getItem("tema") === "oscuro" ? "dark" : null);
      if (legacy) localStorage.setItem("client-theme", legacy === "dark" ? "dark" : "light");
    }
    for (const k of ["theme-preference", "theme", "darkMode", "tema"]) {
      localStorage.removeItem(k);
    }

    const stored = localStorage.getItem("client-theme") ?? "light";
    this.isDarkTheme = stored === "dark";
    this.updateThemeAttributes();
  }
  private updateThemeAttributes() {
    const themeValue = this.isDarkTheme ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", themeValue);
    document.documentElement.setAttribute("theme", themeValue);
    document.body.setAttribute("data-theme", themeValue);
    document.body.setAttribute("theme", themeValue);

    const grad = this.isDarkTheme
      ? "linear-gradient(135deg,#f97316,#ea580c)"
      : "linear-gradient(135deg,#0ea5e9,#94a3b8)";
    document.documentElement.style.setProperty("--theme-grad", grad);

    try {
      localStorage.setItem("client-theme", themeValue);
    } catch { }
  }
  private toggleTheme = () => {
    if (this.isSweetAlertActive) return;
    this.isDarkTheme = !this.isDarkTheme;
    this.updateThemeAttributes();
    window.dispatchEvent(
      new CustomEvent("theme-changed", { detail: { isDark: this.isDarkTheme } })
    );
  };

  /* ========= User / permisos ========= */
  private loadUserData() {
    try {
      const userString = localStorage.getItem("user");
      const role = localStorage.getItem("role");
      if (userString && role) {
        this.userData = JSON.parse(userString);
        this.usuario = role as Rol;
      } else {
        this.userData = null;
        this.usuario = "invitado";
      }
    } catch {
      this.userData = null;
      this.usuario = "invitado";
    }
  }

  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  }
  private tienePermiso(
    permisos: MenuPermiso[],
    permisoBuscado: string
  ): boolean {
    const permisoNormalizado = this.normalizeText(permisoBuscado);
    return permisos.some(
      (p) => this.normalizeText(p.codigo) === permisoNormalizado
    );
  }

  /** Verifica si el menú del usuario contiene un ítem widget (credito/carrito) habilitado */
  private tieneWidgetPermiso(widgetNombre: string): boolean {
    if (!this.userData?.menu) return true; // sin restricciones si no hay menú
    const n = this.normalizeText(widgetNombre);
    return this.userData.menu.some((item) => this.normalizeText(item.nombre) === n);
  }

  private convertBackendUrl(backendUrl: string): string {
    const urlMap: Record<string, string> = {
      "/ofertas/perfumeria": "/clients/offers/perfumeria",
      "/ofertas/medicamentos": "/clients/offers/medicaments",
      "/ofertas/lanzamientos": "/clients/offers/releases",
      "/ofertas/novedades-medicamentos": "/clients/offers/news",
      "/ofertas/novedades-perfumeria": "/clients/offers/perfumeria-news",
      "/pedidos/mis": "/clients/pedidos/ver",
      "/pedidos/enviar-archivo": "/clients/pedidos/enviar",
      "/cuenta/corriente": "/clients/cuenta-corriente",
      "/cuenta/comprobantes": "/clients/comprobantes",
      "/cuenta/resumenes": "/clients/resumenes",
      "/cuenta/recibos": "/clients/recibos",
      "/cuenta/informar-pago": "/clients/pagos/informar",
      "/cuenta/acreditacion": "/clients/acreditacion-tarjetas",
      "/cuenta/reclamos": "/clients/reclamos",
    };
    return urlMap[backendUrl] || backendUrl;
  }

  /* ========= AJUSTE: Unificar "Novedades …" en UNA sola opción ========= */
  // Ítems que ya tienen su widget dedicado en el header derecho — no deben
  // aparecer como links en el navbar central.
  private readonly WIDGET_ITEMS = ["credito", "carrito", "credit", "cart"];

  private esItemWidget(nombre: string): boolean {
    const n = this.normalizeText(nombre);
    return this.WIDGET_ITEMS.some((w) => n === w || n.startsWith(w + "_") || n.endsWith("_" + w));
  }

  private getMenuOptions(): Opcion[] {
    if (!this.userData?.menu) return [];

    const opciones: Opcion[] = [];

    this.userData.menu
      .slice()
      .sort((a, b) => a.orden - b.orden)
      .forEach((item) => {
        // Ítems que controlan widgets del header (crédito, carrito) no van al navbar
        if (this.esItemWidget(item.nombre)) return;
        const opcion: Opcion = {
          texto: item.nombre,
          url: item.url || "#",
          options: null,
        };

        // Home normalizado
        if (item.nombre.toLowerCase() === "inicio") {
          opcion.url = "/clients/home";
          opcion.isHome = true;
        }

        const itemNormalized = this.normalizeText(item.nombre);
        let visible = this.tienePermiso(
          item.permisos,
          `cliente.${itemNormalized}.view`
        );

        if (item.opciones?.length) {
          const visibles = item.opciones
            .filter((sub) =>
              this.tienePermiso(
                sub.permisos,
                `cliente.${itemNormalized}.${this.normalizeText(
                  sub.nombre
                )}.view`
              )
            )
            .sort((a, b) => a.orden - b.orden);

          const subops: SubOpcion[] = [];
          let yaAgregueNovedadesUnificada = false;

          for (const sub of visibles) {
            const subNorm = this.normalizeText(sub.nombre);
            const esMenuOfertas = itemNormalized === "ofertas";

            // ❌ Ocultar "Lanzamientos" del dropdown de Ofertas
            if (esMenuOfertas && subNorm === "lanzamientos") continue;

            // ✅ Unificar "Novedades ..." en un solo item
            const esNovedad =
              esMenuOfertas &&
              (subNorm === "novedades_medicamentos" ||
                subNorm === "novedades_perfumeria");

            if (esNovedad) {
              if (!yaAgregueNovedadesUnificada) {
                subops.push({ texto: "Novedades Ofertas", url: "/clients/offers/news" });
                yaAgregueNovedadesUnificada = true;
              }
              continue; // salteo los originales
            }

            // ❌ RESTRICCIONES ROL 'cliente_user' (Cuenta)
            // Ocultar "Informar un Pago" y "Reclamos"
            if (this.userData?.rol === "cliente_user" && itemNormalized === "cuenta") {
              if (subNorm.includes("informar") || subNorm.includes("reclamos")) {
                continue;
              }
            }

            // Resto de subopciones
            subops.push({
              texto: sub.nombre,
              url: this.convertBackendUrl(sub.url),
            });
          }

          if (subops.length) {
            opcion.options = subops;
            visible = true; // si hay subopciones visibles, mostramos el menú padre
          }
        }

        if (visible) opciones.push(opcion);
      });

    // Agrego Contacto para roles que no sean cliente_user
    if (this.userData.rol !== "cliente_user") {
      opciones.push({ texto: "Contacto", url: "/clients/contact", options: null });
    }

    return opciones;
  }


  private handleHomeClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const searchHome = document.querySelector("search-home") as any;
    if (searchHome && typeof searchHome.limpiarBusqueda === "function") {
      searchHome.limpiarBusqueda();
    }

    this.closeOffcanvas();

    const currentHash = window.location.hash;
    if (currentHash === "#/clients/home" || currentHash === "") {
      window.dispatchEvent(
        new CustomEvent("home-refresh", {
          detail: { fromHeader: true },
        })
      );
      return;
    }

    window.location.hash = "#/clients/home";
  };

  /* ========= Events ========= */
  private setupEventListeners(add: boolean) {
    const w = window;
    const method = add ? w.addEventListener : w.removeEventListener;

    method.call(w, "scroll", this.onScroll as any, { passive: true });
    method.call(w, "resize", this.onResize as any, { passive: true });
    method.call(w, "storage", this.onStorage as any);
    method.call(w, "hashchange", this.onHashChange as any);

    method.call(w, "auth:session-ready", this.onSessionReady as any);
    method.call(w, "auth:session-cleared", this.onAuthCleared as any);
    method.call(w, "app:logout", this.onAppLogout as any);

    (add ? document.addEventListener : document.removeEventListener)(
      "click",
      this.onDocumentClick as any
    );
  }

  private onAuthCleared = (_e: Event) => {
    this.onAppLogout();
  };

  private onSessionReady = (_e: Event) => {
    this.loadUserData();
  };
  private onAppLogout = () => {
    this.closeOffcanvas();
    this.loadUserData();
    this.isDarkTheme = false;
    this.updateThemeAttributes();
  };

  private onDocumentClick = (e: MouseEvent) => {
    if (this.isSweetAlertActive) return;
    const target = e.target as HTMLElement;
    const isInsideDesktopDropdown = !!target.closest(
      ".navbar-nav-desktop .dropdown"
    );
    const isInsideOffcanvas = !!target.closest(".offcanvas");
    if (!isInsideDesktopDropdown && !isInsideOffcanvas)
      this.closeAllDropdowns();
  };
  private onStorage = () => {
    this.loadUserData();
    this.setupTheme();
  };
  private onHashChange = () => {
    this.currentHash = window.location.hash;
    this.loadUserData();
  };

  private isNavLinkActive(url: string): boolean {
    const currentHash = window.location.hash || "#/clients/home";
    const normalizedUrl = url.startsWith("/") ? url : "/" + url;
    const normalizedHash = currentHash.replace(/^#/, "");
    return normalizedHash === normalizedUrl || (normalizedUrl === "/clients/home" && (normalizedHash === "" || normalizedHash === "/clients/home"));
  }

  private isDropdownActive(options: SubOpcion[] | null | undefined): boolean {
    if (!options?.length) return false;
    return options.some((opt) => this.isNavLinkActive(opt.url));
  }

  private onScroll = () => {
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    this._scrollRAF = requestAnimationFrame(() => {
      this.scrolled = window.scrollY > 20;
      const nav = this.querySelector(".modern-compact-nav");
      if (nav) nav.classList.toggle("scrolled", this.scrolled);
    });
  };
  private onResize = () => {
    const isDesktop = window.innerWidth >= 992;
    if (isDesktop) this.closeOffcanvas();
  };

  private handleNavClick(e: Event) {
    if (this.isSweetAlertActive) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  }

  /* ========= Desktop dropdown ========= */
  private closeAllDropdowns = () => {
    if (this.isSweetAlertActive) return;
    const dropdowns = this.querySelectorAll(
      ".navbar-nav-desktop .dropdown-menu"
    );
    dropdowns.forEach((menu) => {
      menu.classList.remove("show");
      const toggle = menu.previousElementSibling as HTMLElement | null;
      if (toggle) toggle.setAttribute("aria-expanded", "false");
    });
  };
  private toggleDesktopDropdown(e: Event) {
    if (this.isSweetAlertActive) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const button =
      (e.currentTarget as HTMLElement) ?? (e.target as HTMLElement);
    const dropdown = button.nextElementSibling as HTMLElement;
    if (!dropdown) return;
    const isOpen = dropdown.classList.contains("show");
    if (isOpen) {
      dropdown.classList.remove("show");
      button.setAttribute("aria-expanded", "false");
      return;
    }
    this.closeAllDropdowns();
    dropdown.classList.add("show");
    button.setAttribute("aria-expanded", "true");
  }

  /* ========= Mobile (offcanvas) ========= */
  private closeAllMobileDropdowns = () => {
    const oc = this.querySelector(".offcanvas");
    if (!oc) return;
    oc.querySelectorAll<HTMLElement>(".dropdown-menu").forEach((m) =>
      m.classList.remove("show")
    );
    oc.querySelectorAll<HTMLElement>(".dropdown-toggle").forEach((t) =>
      t.setAttribute("aria-expanded", "false")
    );
  };
  private toggleOffcanvas = () => {
    if (this.isSweetAlertActive) return;
    const offcanvas = this.querySelector(".offcanvas");
    const backdrop = this.querySelector(".offcanvas-backdrop");
    if (offcanvas && backdrop) {
      this.closeAllMobileDropdowns();
      offcanvas.classList.toggle("show");
      backdrop.classList.toggle("show");
      document.body.style.overflow = offcanvas.classList.contains("show")
        ? "hidden"
        : "";
    }
  };
  private closeOffcanvas = () => {
    this.closeAllMobileDropdowns();
    const offcanvas = this.querySelector(".offcanvas");
    const backdrop = this.querySelector(".offcanvas-backdrop");
    if (offcanvas && backdrop) {
      offcanvas.classList.remove("show");
      backdrop.classList.remove("show");
      document.body.style.overflow = "";
    }
  };
  private handleCartClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.hash = "#/clients/cart";
  };
  private toggleMobileDropdown(e: Event) {
    if (this.isSweetAlertActive) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const button =
      (e.currentTarget as HTMLElement) ?? (e.target as HTMLElement);
    const dropdown = button.nextElementSibling as HTMLElement;
    if (!dropdown) return;

    const isVisible = dropdown.classList.contains("show");
    const oc = this.querySelector(".offcanvas");
    oc?.querySelectorAll(".offcanvas-nav .dropdown-menu").forEach((menu) => {
      if (menu !== dropdown) {
        menu.classList.remove("show");
        const toggle = menu.previousElementSibling as HTMLElement;
        if (toggle) toggle.setAttribute("aria-expanded", "false");
      }
    });

    dropdown.classList.toggle("show", !isVisible);
    button.setAttribute("aria-expanded", String(!isVisible));
  }

  render() {
    const opciones = this.getMenuOptions();
    const nombreUsuario =
      this.userData?.usuario_nombre ||
      this.userData?.cod_farmacia ||
      this.usuario;

    const nombreCliente = this.userData?.nombre || nombreUsuario;
    const mostrarBienvenida = this.usuario !== "invitado" && nombreCliente;

    return html`
      <nav
        class="modern-compact-nav ${this.scrolled ? "scrolled" : ""} ${this
        .isSweetAlertActive
        ? "sweetalert-active"
        : ""}">
        ${mostrarBienvenida
        ? html`
              <div class="welcome-bar-integrated">
                <div class="welcome-text"></div>
                <div class="welcome-controls">
                  <div class="welcome-account-wrapper">
                    <info-cliente usuario="${nombreCliente}"></info-cliente>
                  </div>
                  <button
                    class="theme-toggle"
                    @click=${this.toggleTheme}
                    aria-label="Cambiar tema">
                    ${this.isDarkTheme
            ? html`<svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                        </svg>`
            : html`<svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364-1.414-1.414M6.05 6.05 4.636 4.636m12.728 0L17.95 6.05M6.05 17.95l-1.414 1.414M12 8a4 4 0 100 8 4 4 0 000-8z" />
                        </svg>`}
                  </button>
                </div>
              </div>
            `
        : ""}
        <div class="navbar-main">
          <div class="navbar-container">
            <div class="navbar-brand" @click=${this.handleHomeClick}>
              <img src="${logo}" alt="Logo Droguería Suiza" />
            </div>
            <ul class="navbar-nav-desktop">
              ${opciones.map((opcion) =>
          opcion.options?.length
            ? html` <li class="nav-item dropdown">
                      <button
                        class="dropdown-toggle ${this.isDropdownActive(opcion.options) ? "active" : ""}"
                        type="button"
                        aria-expanded="false"
                        @click=${(e: Event) => this.toggleDesktopDropdown(e)}
                        @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  this.toggleDesktopDropdown(e as any);
                }
              }}>
                        ${opcion.texto}
                      </button>
                      <ul class="dropdown-menu">
                        ${opcion.options.map(
                (sub) => html` <li>
                            <a
                              class="dropdown-item ${this.isNavLinkActive(sub.url) ? "active" : ""}"
                              data-link
                              href="${"#" + sub.url}"
                              @click=${(e: Event) => {
                    if (this.handleNavClick(e))
                      this.closeAllDropdowns();
                  }}>
                              ${sub.texto}
                            </a>
                          </li>`
              )}
                      </ul>
                    </li>`
            : html` <li class="nav-item">
                      ${opcion.isHome
                ? html`<button
                            class="nav-link ${this.isNavLinkActive(opcion.url) ? "active" : ""}"
                            @click=${this.handleHomeClick}>
                            ${opcion.texto}
                          </button>`
                : html`<a
                            class="nav-link ${this.isNavLinkActive(opcion.url) ? "active" : ""}"
                            data-link
                            href="${"#" + opcion.url}"
                            @click=${this.handleNavClick}
                            >${opcion.texto}</a
                          >`}
                    </li>`
        )}
            </ul>

            <div class="user-controls show-desktop">
              ${this.userData?.rol === "cliente_user" || !this.tieneWidgetPermiso("credito")
        ? html`<div style="font-size: 0.75rem; color: #64748b; font-weight: 600; padding: 0.5rem;">CRÉDITO NO DISPONIBLE</div>`
        : html`<credito-header></credito-header>`}

              ${this.tieneWidgetPermiso("carrito")
        ? html`<div class="cart-wrapper" @click=${this.handleCartClick}>
                  <carrito-cliente></carrito-cliente>
                </div>`
        : ""}
            </div>

            <button
              class="navbar-toggler"
              @click=${this.toggleOffcanvas}
              aria-label="Abrir menú">
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
            </button>
          </div>
        </div>
      </nav>

      <div class="offcanvas-backdrop" @click=${this.closeOffcanvas}></div>

      <div class="offcanvas" id="mobileNav" aria-hidden="true">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title">Menú</h5>
          <button
            class="theme-toggle"
            @click=${this.toggleTheme}
            aria-label="Cambiar tema">
            ${this.isDarkTheme
        ? html`<svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>`
        : html`<svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>`}
          </button>
          <button
            class="btn-close"
            @click=${this.closeOffcanvas}
            aria-label="Cerrar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        ${mostrarBienvenida
        ? html`
              <div class="offcanvas-user-section">
                <info-cliente usuario="${nombreCliente}"></info-cliente>
              </div>
            `
        : ""}
        <div class="offcanvas-body">
          <ul class="offcanvas-nav">
            ${opciones.map((opcion) =>
          opcion.options?.length
            ? html` <li class="nav-item">
                    <button
                      class="dropdown-toggle ${this.isDropdownActive(opcion.options) ? "active" : ""}"
                      type="button"
                      aria-expanded="false"
                      @click=${(e: Event) => this.toggleMobileDropdown(e)}
                      @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  this.toggleMobileDropdown(e as any);
                }
              }}>
                      ${opcion.texto}
                    </button>
                    <ul class="dropdown-menu">
                      ${opcion.options.map(
                (sub) => html` <li>
                          <a
                            class="dropdown-item ${this.isNavLinkActive(sub.url) ? "active" : ""}"
                            data-link
                            href="${"#" + sub.url}"
                            @click=${(e: Event) => {
                    if (this.handleNavClick(e)) this.closeOffcanvas();
                  }}>
                            ${sub.texto}
                          </a>
                        </li>`
              )}
                    </ul>
                  </li>`
            : html` <li class="nav-item">
                    ${opcion.isHome
                ? html`<button
                          class="nav-link ${this.isNavLinkActive(opcion.url) ? "active" : ""}"
                          @click=${this.handleHomeClick}>
                          ${opcion.texto}
                        </button>`
                : html`<a
                          class="nav-link ${this.isNavLinkActive(opcion.url) ? "active" : ""}"
                          data-link
                          href="${"#" + opcion.url}"
                          @click=${(e: Event) => {
                    if (this.handleNavClick(e)) this.closeOffcanvas();
                  }}
                          >${opcion.texto}</a
                        >`}
                  </li>`
        )}
          </ul>

          <div class="offcanvas-controls show-mobile">
            <div class="offcanvas-actions">
              ${this.userData?.rol === "cliente_user" || !this.tieneWidgetPermiso("credito")
        ? html`<div style="font-size: 0.75rem; color: #64748b; font-weight: 600; padding: 0.5rem;">CRÉDITO NO DISPONIBLE</div>`
        : html`<credito-header></credito-header>`}

              ${this.tieneWidgetPermiso("carrito")
        ? html`<div class="cart-wrapper" @click=${(e: Event) => { this.handleCartClick(e); this.closeOffcanvas(); }}>
                  <carrito-cliente></carrito-cliente>
                </div>`
        : ""}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
